import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  BarChart3, Users, UserCheck, UserX, Send, Trophy, Target,
  ArrowLeft, TrendingUp, Clock, Briefcase, CheckCircle, AlertCircle,
  Loader2, RefreshCw, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const InsightCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <p className={`text-3xl font-bold ${color}`}>{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color.replace('text-', 'bg-').replace('600', '100')}`}>
        <Icon className={color} size={24} />
      </div>
    </div>
    {trend !== undefined && (
      <div className="mt-3 flex items-center gap-1 text-sm">
        <TrendingUp size={14} className={trend >= 0 ? 'text-green-500' : 'text-red-500'} />
        <span className={trend >= 0 ? 'text-green-600' : 'text-red-600'}>
          {trend >= 0 ? '+' : ''}{trend}%
        </span>
        <span className="text-gray-400">vs last month</span>
      </div>
    )}
  </div>
);

const FunnelBar = ({ label, value, maxValue, color }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="font-semibold text-gray-900">{value}</span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};

const InterviewInsightsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  
  const token = localStorage.getItem('company_token');

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchInsights();
  }, [token, navigate]);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/company/interview-insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(res.data);
    } catch (err) {
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  const funnel = insights?.funnel || {};
  const rates = insights?.conversion_rates || {};
  const challengeStats = insights?.challenge_breakdown || [];
  const statusDist = insights?.status_distribution || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/company/shortlisted" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="text-purple-600" size={24} />
                  Interview Insights
                </h1>
                <p className="text-sm text-gray-500">Hiring funnel analytics & metrics</p>
              </div>
            </div>
            <button
              onClick={fetchInsights}
              className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors"
              data-testid="refresh-insights-btn"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <InsightCard
            title="Total Applicants"
            value={funnel.total_applicants || 0}
            subtitle="Across all challenges"
            icon={Users}
            color="text-blue-600"
          />
          <InsightCard
            title="Shortlisted"
            value={funnel.shortlisted || 0}
            subtitle={`${rates.shortlist_rate || 0}% of applicants`}
            icon={Target}
            color="text-purple-600"
          />
          <InsightCard
            title="Hired"
            value={funnel.hired || 0}
            subtitle={`${rates.hire_rate || 0}% offer acceptance`}
            icon={Trophy}
            color="text-green-600"
          />
          <InsightCard
            title="Rejected"
            value={funnel.rejected || 0}
            subtitle="Not proceeding"
            icon={UserX}
            color="text-red-600"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Hiring Funnel */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="text-purple-600" size={20} />
              Hiring Funnel
            </h2>
            <div className="space-y-4">
              <FunnelBar 
                label="Total Applicants" 
                value={funnel.total_applicants || 0} 
                maxValue={funnel.total_applicants || 1}
                color="bg-blue-500"
              />
              <FunnelBar 
                label="Shortlisted" 
                value={funnel.shortlisted || 0} 
                maxValue={funnel.total_applicants || 1}
                color="bg-purple-500"
              />
              <FunnelBar 
                label="Contacted" 
                value={funnel.contacted || 0} 
                maxValue={funnel.total_applicants || 1}
                color="bg-indigo-500"
              />
              <FunnelBar 
                label="Interviewed" 
                value={funnel.interviewed || 0} 
                maxValue={funnel.total_applicants || 1}
                color="bg-amber-500"
              />
              <FunnelBar 
                label="Offered" 
                value={funnel.offered || 0} 
                maxValue={funnel.total_applicants || 1}
                color="bg-emerald-500"
              />
              <FunnelBar 
                label="Hired" 
                value={funnel.hired || 0} 
                maxValue={funnel.total_applicants || 1}
                color="bg-green-600"
              />
            </div>
          </div>

          {/* Conversion Rates */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="text-green-600" size={20} />
              Conversion Rates
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-purple-600">{rates.shortlist_rate || 0}%</p>
                <p className="text-sm text-purple-700 mt-1">Shortlist Rate</p>
                <p className="text-xs text-purple-500">Applicants → Shortlisted</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{rates.interview_rate || 0}%</p>
                <p className="text-sm text-blue-700 mt-1">Interview Rate</p>
                <p className="text-xs text-blue-500">Shortlisted → Interviewed</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-amber-600">{rates.offer_rate || 0}%</p>
                <p className="text-sm text-amber-700 mt-1">Offer Rate</p>
                <p className="text-xs text-amber-500">Interviewed → Offered</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{rates.hire_rate || 0}%</p>
                <p className="text-sm text-green-700 mt-1">Hire Rate</p>
                <p className="text-xs text-green-500">Offered → Hired</p>
              </div>
            </div>
            
            {/* Status Distribution */}
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Current Status Distribution</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(statusDist).map(([status, count]) => (
                  <span 
                    key={status}
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      status === 'hired' ? 'bg-green-100 text-green-700' :
                      status === 'offered' ? 'bg-amber-100 text-amber-700' :
                      status === 'interviewed' ? 'bg-purple-100 text-purple-700' :
                      status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                      status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {status}: {count}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Challenge-wise Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Briefcase className="text-indigo-600" size={20} />
            Challenge-wise Performance
          </h2>
          {challengeStats.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Briefcase size={48} className="mx-auto mb-3 opacity-30" />
              <p>No hiring challenges found</p>
              <p className="text-sm">Create a hiring challenge to see analytics</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Challenge</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Applicants</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Shortlisted</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Hired</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Conversion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {challengeStats.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900">{c.title}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-blue-600 font-semibold">{c.total_applicants}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-purple-600 font-semibold">{c.shortlisted}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-green-600 font-semibold">{c.hired}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                          c.conversion_rate >= 10 ? 'bg-green-100 text-green-700' :
                          c.conversion_rate >= 5 ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {c.conversion_rate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="text-amber-600" size={20} />
            Recent Activity
          </h2>
          <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <UserCheck className="text-amber-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">
                {insights?.recent_activity?.shortlisted_last_30_days || 0}
              </p>
              <p className="text-sm text-amber-700">Candidates shortlisted in last 30 days</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-4">
          <Link
            to="/company/shortlisted"
            className="flex-1 p-4 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl text-white hover:shadow-lg transition-all flex items-center justify-between"
          >
            <div>
              <p className="font-semibold">Manage Candidates</p>
              <p className="text-purple-200 text-sm">View and email shortlisted candidates</p>
            </div>
            <ChevronRight size={24} />
          </Link>
          <Link
            to="/company/hiring-challenges"
            className="flex-1 p-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl text-white hover:shadow-lg transition-all flex items-center justify-between"
          >
            <div>
              <p className="font-semibold">Hiring Challenges</p>
              <p className="text-green-200 text-sm">Create and manage challenges</p>
            </div>
            <ChevronRight size={24} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InterviewInsightsPage;
